import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import {lbColors, lbRadius, lbShadow} from '../leaderboardTheme';
import {MeVM, PeriodMeta} from '../types';
import PlayerAvatar from './PlayerAvatar';
import TrendPill from './TrendPill';

interface Props {
  me: MeVM;
  meta: PeriodMeta;
  totalPlayers: number;
  leaderWins: number;
  // Engagement (streak & rewards) row — omitted when the page's engagement
  // sections are gated off (page_enabled=false or the summary failed).
  streakCount?: number;
  streakLine?: string;
  onPressStreak?: () => void;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Wide-layout right rail: the dark "YOUR RANK" panel with Wins/Rating stats
 * plus the white "THIS DAY/WEEK" mini-stats card.
 */
export default function YourRankPanel({
  me,
  meta,
  totalPlayers,
  leaderWins,
  streakCount,
  streakLine,
  onPressStreak,
}: Props) {
  const trendWord =
    me.trend === 'up'
      ? 'up since yesterday'
      : me.trend === 'down'
      ? 'down since yesterday'
      : 'no change';
  // A point-in-time streak has no meaningful "since yesterday" movement.
  const metricWord = meta.metricLabel === 'DAYS' ? 'days' : 'wins';

  return (
    <View style={styles.rail}>
      <View style={styles.rankCardDark}>
        <CCText
          fontSize={12}
          fontWeight="bold"
          color={lbColors.gold}
          style={styles.rankDarkLabel}>
          YOUR RANK · {meta.label.toUpperCase()}
        </CCText>
        <View style={styles.rankDarkTop}>
          <PlayerAvatar
            initials={me.initials}
            color={lbColors.teal}
            avatarUrl={me.avatarUrl}
            size={58}
            ring={lbColors.gold}
          />
          <View>
            <CCText fontSize={38} fontWeight="bold" color="#FFFFFF">
              {me.rank != null ? `#${me.rank}` : '—'}
            </CCText>
            {meta.showTrend ? (
              <View style={styles.trendRow}>
                <TrendPill trend={me.trend} value={me.trendAbs} />
                <CCText
                  fontSize={13}
                  fontWeight="semibold"
                  color={lbColors.darkText}>
                  {trendWord}
                </CCText>
              </View>
            ) : null}
          </View>
        </View>
        <View style={styles.statRow}>
          <Stat value={me.wins} label={capitalize(metricWord)} />
          <Stat value={me.rating} label="Rating" />
        </View>
        {streakCount != null ? (
          <Pressable
            onPress={onPressStreak}
            style={({pressed}) => [
              styles.streakRow,
              pressed && {opacity: 0.85},
            ]}>
            <CCText fontSize={19}>🔥</CCText>
            <View style={{flex: 1, minWidth: 0}}>
              <CCText fontSize={13} fontWeight="bold" color={lbColors.gold}>
                {streakCount}-day streak
              </CCText>
              <CCText
                fontSize={10}
                fontWeight="semibold"
                color={lbColors.darkText}>
                {streakLine}
              </CCText>
            </View>
            <CCText fontSize={17}>🎁</CCText>
          </Pressable>
        ) : null}
      </View>

      <View style={[styles.miniCard, lbShadow.card]}>
        <CCText
          fontSize={12}
          fontWeight="bold"
          color={lbColors.faint}
          style={styles.miniLabel}>
          THIS {meta.word.toUpperCase()}
        </CCText>
        <MiniStat label="Players competing" value={totalPlayers} />
        <MiniStat label={`Leader's ${metricWord}`} value={leaderWins} />
        <MiniStat
          label={`${capitalize(metricWord)} to next rank`}
          value={me.rank != null ? me.toNext : '—'}
          accent
        />
      </View>
    </View>
  );
}

function Stat({value, label}: {value: number | string; label: string}) {
  return (
    <View style={styles.stat}>
      <CCText fontSize={22} fontWeight="bold" color="#FFFFFF">
        {value}
      </CCText>
      <CCText
        fontSize={11.5}
        fontWeight="semibold"
        color={lbColors.darkText}
        style={{marginTop: 2}}>
        {label}
      </CCText>
    </View>
  );
}

function MiniStat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
}) {
  return (
    <View style={styles.miniStat}>
      <CCText fontSize={14} fontWeight="semibold" color={lbColors.inkSoft}>
        {label}
      </CCText>
      <CCText
        fontSize={16}
        fontWeight="bold"
        color={accent ? lbColors.orange : lbColors.ink}>
        {value}
      </CCText>
    </View>
  );
}

const styles = StyleSheet.create({
  rail: {width: 320, gap: 20},
  rankCardDark: {
    backgroundColor: lbColors.darkPanel,
    borderRadius: lbRadius.xl + 2,
    padding: 26,
  },
  rankDarkLabel: {letterSpacing: 0.6, marginBottom: 18},
  rankDarkTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 22,
  },
  trendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 3,
  },
  statRow: {flexDirection: 'row', gap: 10, marginBottom: 9},
  streakRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    backgroundColor: 'rgba(245,195,60,0.13)',
    borderRadius: lbRadius.md,
    padding: 12,
  },
  stat: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderRadius: lbRadius.md,
    padding: 14,
  },
  miniCard: {
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.xl + 2,
    padding: 24,
  },
  miniLabel: {letterSpacing: 0.5, marginBottom: 12},
  miniStat: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: lbColors.lineSoft,
  },
});
